import React, { useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from 'formik';
import Button from "../../../components/buttons/Button";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import InputField from "../../../components/inputFields/InputField";
import { toast } from "react-toastify";
import FormikDropdown from "../../../components/inputFields/FormikDropDown";
import { newAddHudPatientDoses, newUpdateHudPatientDoses } from "../../../services/new_clinic/new_hud_doses";
import { sendInvoiceVericifationOtp } from "../../../services/clinic/invoice";

const NewAddUpdateDose = ({ packageId, onDoseOperation, initialValues, packageName, requireOtp = false, phoneNumber = "", altNumber = "" }) => {
    // TPT doses need the patient's registered mobile number verified before a dose can be added.
    const [otpSent, setOtpSent] = useState(false);
    const [serverOtp, setServerOtp] = useState(null);
    const [otpInput, setOtpInput] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(null); // "primary" | "alt" | null

    const altNumberClean = (altNumber || "").trim();
    const needsOtp = requireOtp && !initialValues;
    const otpCleared = !needsOtp || otpVerified;

    const handleSendOtp = async (target) => {
        const number = target === "alt" ? altNumberClean : phoneNumber;
        if (target === "alt" && number.length < 10) {
            toast.error("No valid alternate number on file for this patient");
            return;
        }
        if (target !== "alt" && !phoneNumber) {
            toast.error("No mobile number on file for this patient");
            return;
        }
        setSendingOtp(target);
        try {
            const formData = new FormData();
            formData.append("mobile_number", number);
            const response = await sendInvoiceVericifationOtp(formData);
            if (response?.status === 200) {
                setServerOtp(String(response.data.otp));
                setOtpSent(true);
                setOtpVerified(false);
                setOtpInput("");
                toast.success(`OTP sent to ${number}`);
            } else {
                toast.error("Unable to send OTP, please try again");
            }
        } catch (error) {
            const errors = error?.response?.data?.error || "Unable to send OTP, please try again";
            toast.error(errors);
        } finally {
            setSendingOtp(null);
        }
    };

    const handleVerifyOtp = () => {
        if (!otpInput) {
            toast.error("Enter the OTP");
            return;
        }
        if (otpInput.trim() !== serverOtp) {
            toast.error("Incorrect OTP");
            return;
        }
        setOtpVerified(true);
        toast.success("OTP verified");
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        if (needsOtp && !otpVerified) {
            toast.error("Verify the patient's OTP before adding a dose");
            return;
        }
        setSubmitting(true);
        try {
            const response = initialValues ? await newUpdateHudPatientDoses(packageId, initialValues.dose_id, values) : await newAddHudPatientDoses(packageId, values);
            if (initialValues ? response?.status === 200 : response?.status === 201) {
                onDoseOperation();
                toast.success("Dose added successfully!");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        } finally {
            setSubmitting(false);
        }
    };

    const validationSchema = Yup.object({
        joulepercm: packageName === "FULL BODY"
            ? Yup.number()
                .typeError("Only numbers are allowed")
                .required("Energy is required")
                .when("tube", {
                    is: "Tube A",
                    then: (schema) => schema.max(15, "For Tube A, max is 15"),
                })
                .when("tube", {
                    is: "Tube B",
                    then: (schema) => schema.max(1500, "For Tube B, max is 1500"),
                })
                .when("tube", {
                    is: "No Tube",
                    then: (schema) => schema.oneOf([0], "For No Tube, value must be 0"),
                })
            : Yup.string().required("Energy is required"),
        tube: Yup.string().required("Tube is required"),
    });

    return (
        <>
            <div className="w-full flex-1">
                <StyledHeadingText text={initialValues ? "Update Dose" : "Add Dose"} />

                {needsOtp && (
                    <div className={`my-4 rounded-lg border p-4 ${otpVerified ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`}>
                        {otpVerified ? (
                            <p className="text-sm font-semibold text-green-700">
                                OTP verified for {phoneNumber} — you can now add the dose.
                            </p>
                        ) : (
                            <>
                                <p className="text-sm font-semibold text-gray-800">Verify Patient OTP</p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    A TPT dose can be added only after verifying an OTP sent to the patient's
                                    registered mobile number.
                                </p>

                                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600">Registered Mobile Number</label>
                                        <input
                                            type="text"
                                            value={phoneNumber || "Not available"}
                                            readOnly
                                            disabled
                                            className="mt-1 w-48 cursor-not-allowed rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-600"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        className="border border-primary bg-white text-xs text-primary"
                                        onClick={() => handleSendOtp("primary")}
                                        disabled={sendingOtp !== null || !phoneNumber}
                                    >
                                        {sendingOtp === "primary" ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
                                    </Button>
                                </div>

                                {altNumberClean && (
                                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600">Alternate Number</label>
                                            <input
                                                type="text"
                                                value={altNumberClean}
                                                readOnly
                                                disabled
                                                className="mt-1 w-48 cursor-not-allowed rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-600"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            className="border border-primary bg-white text-xs text-primary"
                                            onClick={() => handleSendOtp("alt")}
                                            disabled={sendingOtp !== null}
                                        >
                                            {sendingOtp === "alt" ? "Sending..." : "Send to Alt Number"}
                                        </Button>
                                    </div>
                                )}

                                {otpSent && (
                                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600">Enter OTP</label>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={4}
                                                value={otpInput}
                                                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ""))}
                                                placeholder="4-digit OTP"
                                                className="mt-1 w-48 rounded-md border border-gray-300 px-3 py-2 text-sm tracking-widest"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            className="bg-primary text-xs text-white"
                                            onClick={handleVerifyOtp}
                                        >
                                            Verify
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                <Formik
                    initialValues={initialValues || {
                        joulepercm: 0,
                        tube: "No Tube",
                        date: new Date().toISOString().split("T")[0],
                        notes: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form className={`space-y-4 my-4 ${otpCleared ? "" : "pointer-events-none select-none opacity-50"}`}>
                            <div className="flex gap-4 flex-col">
                                <div className="grid gap-6 lg:grid-cols-3">
                                    <FormikDropdown
                                        label="Tube"
                                        name="tube"
                                        placeholder="Select Tube"
                                        required
                                        options={[
                                            { label: "No Tube", value: "No Tube" },
                                            { label: "Tube A", value: "Tube A" },
                                            { label: "Tube B", value: "Tube B" },
                                        ]}
                                    />
                                    <div onClick={() => { setFieldValue("joulepercm", "") }}>
                                        <InputField
                                            label="J/CM²"
                                            name="joulepercm"
                                            type={packageName === "FULL BODY" ? "number" : "text"}
                                            inputMode={packageName === "FULL BODY" ? "decimal" : "text"}
                                            step={packageName === "FULL BODY" ? "any" : undefined}
                                            placeholder="Enter J/CM²"
                                            // required={packageName === "FULL BODY"}
                                            required
                                        />
                                    </div>
                                    <InputField
                                        label="Date"
                                        name="date"
                                        type="date"
                                        placeholder="Select Date"
                                    />
                                </div>
                                <InputField
                                    label="Note"
                                    name="notes"
                                    type="text"
                                    placeholder="Enter Quick Note here..."
                                />
                                <Button
                                    type="submit"
                                    className="bg-primary text-white px-4 py-2 rounded max-w-32"
                                    disabled={isSubmitting || !otpCleared}
                                >
                                    {initialValues ? "Update" : "Add"}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div >
        </>
    );
};

export default NewAddUpdateDose;
